import { useCallback, useEffect, useState } from 'react';
import { stockAuditService } from '../../../services/stockAuditService';
import { extractErrorMessage } from '../../../utils/errorUtils';
import { isUnexpectedResult } from './scanResultLabels';
import type {
  ScanOutcome,
  StockAuditDetails,
} from '../../../types/stockAudit';

const addScanToDetails = (
  prev: StockAuditDetails,
  outcome: ScanOutcome,
): StockAuditDetails => {
  if (!outcome.scan) {
    return prev;
  }

  const missing = outcome.result === 'FOUND'
    ? prev.missing.filter((item) => item.serialNumber !== outcome.serialNumber)
    : prev.missing;

  return {
    ...prev,
    scans: [outcome.scan, ...prev.scans],
    missing,
    missingCount: missing.length,
    foundCount: prev.foundCount + (outcome.result === 'FOUND' ? 1 : 0),
    unexpectedCount: prev.unexpectedCount + (isUnexpectedResult(outcome.result) ? 1 : 0),
    outOfScopeCount: prev.outOfScopeCount + (outcome.result === 'OUT_OF_SCOPE' ? 1 : 0),
  };
};

export function useStockAuditDetail(auditId: number) {
  // Data
  const [details, setDetails] = useState<StockAuditDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Actions
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await stockAuditService.getDetails(auditId);
      setDetails(data);
      setError(null);
    } catch (err) {
      console.error('Failed to load stock audit:', err);
      setError(extractErrorMessage(err, 'Не вдалося завантажити ревізію'));
    } finally {
      setLoading(false);
    }
  }, [auditId]);

  // After a failed action the audit may have been closed by someone else; refetch without
  // replacing the page with a load error if that fails too
  const refreshAfterFailure = useCallback(async () => {
    try {
      setDetails(await stockAuditService.getDetails(auditId));
    } catch (err) {
      console.error('Failed to refresh stock audit:', err);
    }
  }, [auditId]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const scan = async (serialNumber: string): Promise<ScanOutcome> => {
    try {
      const outcome = await stockAuditService.scan(auditId, serialNumber);
      setDetails((prev) => (prev ? addScanToDetails(prev, outcome) : prev));
      return outcome;
    } catch (err) {
      await refreshAfterFailure();
      throw err;
    }
  };

  // A removed FOUND scan puts the serial back into "missing", which only the server knows
  const removeScan = async (scanId: number) => {
    setActionError(null);
    try {
      await stockAuditService.removeScan(auditId, scanId);
      await load();
    } catch (err) {
      console.error('Failed to remove scan:', err);
      setActionError(extractErrorMessage(err, 'Не вдалося видалити скан'));
    }
  };

  const complete = async () => {
    setBusy(true);
    setActionError(null);
    try {
      setDetails(await stockAuditService.complete(auditId));
    } catch (err) {
      console.error('Failed to complete stock audit:', err);
      setActionError(extractErrorMessage(err, 'Не вдалося закрити ревізію'));
      await refreshAfterFailure();
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (): Promise<boolean> => {
    setBusy(true);
    setActionError(null);
    try {
      await stockAuditService.cancel(auditId);
      return true;
    } catch (err) {
      console.error('Failed to cancel stock audit:', err);
      setActionError(extractErrorMessage(err, 'Не вдалося скасувати ревізію'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  return {
    data: {
      details,
      loading,
      error,
    },
    actions: {
      busy,
      actionError,
      clearActionError: () => setActionError(null),
      scan,
      removeScan,
      complete,
      cancel,
    },
  };
}
